import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Menu, X, LayoutDashboard, Building2, Users, User2, Briefcase, Clock, BarChart3, Shirt, PackageSearch, Settings, UserCog, LogOut, Cog, Zap, Book, BookOpen, FileText, Calendar, DollarSign, MapPin, CheckSquare, Truck, Radio, MonitorCheck, LayoutGrid, Timer } from 'lucide-react';
import { useAuth, MODULES, ROLES } from '@/contexts/AuthContext';
import logo from '@/images/opty_lav.png';
import ThemeToggle from '@/components/ui/ThemeToggle';
import UpdateNotifier from '@/components/UpdateNotifier';
import NotificationBell from '@/components/NotificationBell';
import { fetchLatestVersion, getLocalVersion } from '@/services/version.service';
import WashingMachineIcon from '@/components/icons/WashingMachineIcon';

const Navbar = () => {
  const { user, logout, hasPermission } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const [appVersion, setAppVersion] = useState(null);
  const [versionData, setVersionData] = useState(null);
  const [openVersionModal, setOpenVersionModal] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  const activeLinkClass = "bg-primary/20 text-primary";
  const inactiveLinkClass = "hover:bg-primary/10 hover:text-primary/80";

  useEffect(() => {
    let mounted = true;
    (async () => {
      const v = await fetchLatestVersion();
      if (!mounted) return;
      setAppVersion(v?.version || getLocalVersion() || null);
    })();
    return () => { mounted = false; };
  }, []);

  // Definir los ítems de navegación dinámicamente basados en los permisos
  const navItems = [

    {
      name: 'Dashboard',
      path: '/panel',
      icon: <LayoutDashboard className="h-5 w-5" />,
      module: MODULES.REPORTS
    },
    {
      name: 'Reportes',
      path: '/dashboard',
      icon: <BarChart3 className="h-5 w-5" />,
      module: MODULES.REPORTS
    },
    {
      name: 'Operario',
      path: '/operario-dashboard',
      icon: <Briefcase className="h-5 w-5" />,
      module: MODULES.OPERARIO,
      requiredRole: ROLES.EMPLOYEE,
    },
    {
      name: 'Portal Clientes',
      path: '/cliente-dashboard',
      icon: <User2 className="h-5 w-5" />,
      module: MODULES.CLIENT_PORTAL
    },
    {
      name: 'Centros',
      path: '/centros-lavado',
      icon: <Building2 className="h-5 w-5" />,
      module: MODULES.WORKPLACES,
      // El rol de servicios públicos solo debe entrar al dashboard
      // de SU centro (vía redirect tras login), no al listado general
      // de workplaces. Por eso se le oculta este link del navbar.
      hiddenForRoles: [ROLES.SERVICES_PUBLIC],
    },
    {
      name: 'Conductor',
      path: '/conductor',
      icon: <Truck className="h-5 w-5" />,
      module: MODULES.CONDUCTOR
    },
    {
      name: 'Mis Prendas',
      path: '/mis-prendas',
      icon: <Shirt className="h-5 w-5" />,
      module: MODULES.CLIENT_PRENDAS
    },
    {
      name: 'Administrativo',
      path: '/control-empleados',
      icon: <UserCog className="h-5 w-5" />,
      // El menu solo aparece si el usuario tiene algun modulo realmente
      // administrativo. Sin esto, "Reporte Conductores" (que pide WORKPLACES)
      // mantenia el menu visible aunque se le hubieran quitado todos los
      // permisos administrativos.
      groupModule: [
        MODULES.CONTROL_EMPLOYEES,
        MODULES.EMPLOYEE_PAYROLL,
        MODULES.BILLING,
        MODULES.BILLING_GENERATE,
        MODULES.BILLING_DELETE,
        MODULES.SOLICITUDES,
        MODULES.RFID_REGISTRO,
        MODULES.AUTHORIZED_HOURS,
        MODULES.PERSONAL,
      ],
      subLinks: [
        {
          name: 'Personal',
          path: '/personal',
          icon: Users,
          module: MODULES.PERSONAL
        },
        {
          name: 'Control personal',
          path: '/control-empleados',
          icon: Calendar,
          module: MODULES.CONTROL_EMPLOYEES
        },
        {
          name: 'Nomina',
          path: '/liquidacion-personal',
          icon: DollarSign,
          module: MODULES.EMPLOYEE_PAYROLL
        },
        {
          name: 'Horas autorizadas',
          path: '/horas-autorizadas',
          icon: Timer,
          module: MODULES.AUTHORIZED_HOURS
        },
     /*  uso interno de compañia DLTAUSA  {
          name: 'Liquidación',
          path: '/liquidacion',
          icon: BarChart3,
          module: MODULES.BILLING_GENERATE
        }, */
        {
          name: 'Facturación',
          path: '/facturacion',
          icon: BarChart3,
          module: MODULES.BILLING
        },
        {
          name: 'Tareas',
          path: '/solicitudes-desarrollo',
          icon: CheckSquare,
          module: MODULES.SOLICITUDES
        },
      /*   {
          name: 'Centro de Monitoreo',
          path: '/monitor-registros',
          icon: MonitorCheck,
          module: [MODULES.WORKPLACES, MODULES.REPORTS]
        }, */
        {
          name: 'Reporte Conductores',
          path: '/reportes/conductores',
          icon: MapPin,
          module: MODULES.WORKPLACES
        },
        {
          name: 'Gestión RFID',
          path: '/rfid',
          icon: Radio,
          module: MODULES.RFID_REGISTRO
        }
      ].filter(item => hasPermission(item.module))
    },
    {
      name: 'Financiera',
      path: '/financiera',
      icon: <DollarSign className="h-5 w-5" />,
      module: MODULES.FINANCIERA,
    },
    {
      name: 'Parametrización',
      path: '/parametrizacion',
      icon: <Settings className="h-5 w-5" />,
      module: 'settings',
      subLinks: [
        {
          name: 'Parametrizar por Centro',
          path: '/parametrizacion/centros',
          icon: LayoutGrid,
          module: MODULES.LAUNDRIES_CENTERS
        },
        {
          name: 'Empleados',
          path: '/parametrizacion/empleados',
          icon: Users,
          module: MODULES.EMPLOYEES
        },
  /*       {
          name: 'Tipos de Archivo',
          path: '/parametrizacion/tipos-archivo',
          icon: FileText,
          module: MODULES.DOCUMENT_TYPES
        }, */
        {
          name: 'Productos',
          path: '/parametrizacion/productos',
          icon: PackageSearch,
          module: MODULES.PRODUCTS
        },
        {
          name: 'Centros de Lavando',
          path: '/parametrizacion/centros-lavado',
          icon: Building2,
          module: MODULES.LAUNDRIES_CENTERS
        },
        {
          name: 'Lavadoras',
          path: '/parametrizacion/lavadoras',
          icon: Cog,
          module: 'machines'
        },
        {
          name: 'Secadoras',
          path: '/parametrizacion/secadoras',
          icon: Cog,
          module: 'machines'
        },
        {
          name: 'Tipos de Prenda',
          path: '/parametrizacion/tipos-prenda',
          icon: Shirt,
          module: MODULES.GARMENT_TYPES
        },
        {
          name: 'Prendas Clientes Internos',
          path: '/parametrizacion/prendas-clientes',
          icon: Shirt,
          module: MODULES.CLIENTS
        },
        {
          name: 'Familia de Prendas',
          path: '/parametrizacion/familia-prendas',
          icon: Shirt,
          module: MODULES.GARMENT_TYPES
        },
        {
          name: 'Programas de Lavado',
          path: '/parametrizacion/programas-lavado',
          icon: Book,
          module: 'washing_programs'
        },
        {
          name: 'Clientes',
          path: '/parametrizacion/clientes',
          icon: User2,
          module: 'clients'
        },
        {
          name: 'Servicios',
          path: '/parametrizacion/servicios',
          icon: Zap,
          module: 'services'
        },
        {
          name: 'Proveedores',
          path: '/parametrizacion/proveedores',
          icon: Briefcase,
          module: MODULES.PROVIDERS
        },
        {
          name: 'Usuarios',
          path: '/parametrizacion/usuarios',
          icon: UserCog,
          module: 'users'
        },
        {
          name: 'Vehículos',
          path: '/parametrizacion/vehiculos',
          icon: Truck,
          module: MODULES.VEHICLES
        }
      ].filter(item => hasPermission(item.module))
    },
  ].filter(item => {
    if (item.requiredRole && user?.role !== item.requiredRole) return false;
    if (Array.isArray(item.hiddenForRoles) && item.hiddenForRoles.includes(user?.role)) return false;
    if (item.subLinks) {
      if (item.groupModule && !hasPermission(item.groupModule)) return false;
      return item.subLinks.length > 0;
    }
    return !item.module || hasPermission(item.module);
  });



  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 20 }}
      className="bg-card shadow-lg sticky top-0 z-50"
    >
      {/* Notificador de actualizaciones: verifica nuevas versiones y muestra toast */}
      <UpdateNotifier checkIntervalMs={60000} />
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center space-x-2 pt-0 ">
            <motion.div whileHover={{ rotate: [0, 5, -5, 0] }}>
              <img src={logo} alt="LOGO-PROFLUX-ERP" className="h-20 w-auto" />
            </motion.div>
          </Link>

          <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {navItems.map((item) => {
              const isParentActive = item.subLinks
                ? item.subLinks.some((sub) => location.pathname.startsWith(sub.path))
                : location.pathname.startsWith(item.path);

              return item.subLinks ? (
                <DropdownMenu key={item.name}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className={`flex items-center space-x-2 px-3 py-2 text-base font-medium transition-colors duration-300 rounded-md ${isParentActive
                        ? activeLinkClass
                        : inactiveLinkClass
                        }`}
                    >
                      {item.icon}
                      <span>{item.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {item.subLinks.map((sub) => (
                      <DropdownMenuItem key={sub.name} asChild>
                        <NavLink
                          to={sub.path}
                          className={({ isActive }) =>
                            `w-full flex items-center space-x-2 px-3 py-2 text-base font-medium transition-colors duration-300 rounded-md ${isActive ? activeLinkClass : 'hover:bg-primary/10'
                            }`
                          }
                        >
                          <sub.icon className="h-5 w-5" />
                          <span>{sub.name}</span>
                        </NavLink>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <NavLink
                  key={item.name}
                  to={item.path}
                  className={({ isActive }) =>
                    `flex items-center space-x-2 px-3 py-2 text-base font-medium transition-colors duration-300 rounded-md ${isActive ? activeLinkClass : inactiveLinkClass
                    }`
                  }
                >
                  {item.icon}
                  <span>{item.name}</span>
                </NavLink>
              );
            })}
            {/* Campana de notificaciones de monitoreo */}
      {/*       <NotificationBell /> */}
            {/* Version badge */}
            <button
              type="button"
              onClick={() => setOpenVersionModal(true)}
              className="ml-2 px-2 py-1 text-xs rounded bg-primary/10 text-primary select-none hover:bg-primary/20 transition"
              title="Ver cambios de la versión"
            >
              v{appVersion || '—'}
            </button>

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="ml-2 rounded-full w-10 h-10 p-0">
                    <span className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="sr-only">Menú de usuario</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 text-sm font-medium">
                    <div className="text-foreground">{user.name} - {user.role}</div>
                    <div className="text-xs text-muted-foreground">{user.email}</div>
                    <div className="text-xs text-primary">{user?.lavanderia?.nombre || ''}</div>
                  </div>

                  {/* Theme toggle (desktop) */}
                  <div className="ml-2">
                    <ThemeToggle variant="buttons" />
                  </div>
                  <DropdownMenuItem asChild>
                    <button
                      onClick={() => logout()}
                      className="w-full flex items-center text-red-600 hover:text-red-700"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Cerrar sesión</span>
                    </button>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMenu}
              className="text-foreground hover:text-primary"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              <span className="sr-only">Toggle menu</span>
            </Button>
          </div>
        </div>
      </div>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden absolute top-20 left-0 right-0 bg-card shadow-lg rounded-b-lg p-4 space-y-2 max-h-[calc(100vh-5rem)] overflow-y-auto"
        >
          {/* Theme toggle + campana (mobile) */}
          <div className="flex justify-end items-center gap-2 pb-2">
            <NotificationBell />
            <ThemeToggle variant="select" />
          </div>
          {navItems.map((item) => (
            <React.Fragment key={item.name}>
              <NavLink
                to={item.subLinks ? '#' : item.path}
                onClick={() => !item.subLinks && setIsOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-md text-base font-medium transition-colors duration-300 flex items-center space-x-3 ${isActive && !item.subLinks ? activeLinkClass : inactiveLinkClass
                  }`
                }
              >
                {item.icon}
                <span>{item.name}</span>
              </NavLink>
              {item.subLinks && (
                <div className="pl-4">
                  {item.subLinks.map((sub) => (
                    <NavLink
                      key={sub.name}
                      to={sub.path}
                      onClick={() => setIsOpen(false)}
                      className={({ isActive }) =>
                        `block px-4 py-2 rounded-md text-base font-medium transition-colors duration-300 flex items-center space-x-3 ${isActive ? activeLinkClass : inactiveLinkClass
                        }`
                      }
                    >
                      <sub.icon className="h-5 w-5" />
                      <span>{sub.name}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}

          {user && (
            <div className="border-t border-border pt-4 mt-4">
              <div className="px-4 py-2 text-sm font-medium text-foreground">
                {user.name}
              </div>
              <div className="px-4 text-xs text-muted-foreground mb-3">
                {user.email}
              </div>

              {/* Theme toggle (desktop) */}
              <div className="ml-2">
                <ThemeToggle variant="buttons" />
              </div>
              <button
                onClick={() => {
                  logout();
                  setIsOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md flex items-center space-x-2"
              >
                <LogOut className="h-4 w-4" />
                <span>Cerrar sesión</span>
              </button>
            </div>
          )}
        </motion.div>
      )}
      {/* Version Changelog Modal - top-level in Navbar */}
      <Dialog open={openVersionModal} onOpenChange={setOpenVersionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Versión {appVersion || (versionData?.version ?? '')}</DialogTitle>
            <DialogDescription>
              Novedades y cambios incluidos en esta versión.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {versionData?.changes?.length ? (
              <ul className="list-disc pl-5 space-y-1">
                {versionData.changes.map((c, idx) => (
                  <li key={idx} className="text-sm">{c}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No hay cambios listados.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpenVersionModal(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </motion.nav>
  );
};

export default Navbar;